import { z } from "zod"

const checklistTextQuestionSpecSchema = z.object({
    type: z.literal("text"),
    id: z.string(),
    isRequired: z.boolean(),
    question: z.string(),
    variant: z.enum(["short", "long"]),
})

const checklistMultiSelectQuestionSpecSchema = z.object({
    type: z.literal("multi-select"),
    id: z.string(),
    isRequired: z.boolean(),
    question: z.string(),
    options: z.array(z.string()),
})

const checklistRadioQuestionSpecSchema = z.object({
    type: z.literal("radio"),
    id: z.string(),
    isRequired: z.boolean(),
    question: z.string(),
    options: z.array(z.string()),
})

const checklistHeaderSpecSchema = z.object({
    type: z.literal("header"),
    id: z.string(),
    question: z.string(),
})

const userSelectSpecSchema = z.object({
    type: z.literal("user-select"),
    id: z.string(),
    isRequired: z.boolean(),
    question: z.string(),
})

const aircraftSelectSpecSchema = z.object({
    type: z.literal("aircraft-select"),
    id: z.string(),
    isRequired: z.boolean(),
    question: z.string(),
})

export const checklistQuestionSpecSchema = z.discriminatedUnion("type", [
    checklistTextQuestionSpecSchema,
    checklistMultiSelectQuestionSpecSchema,
    checklistRadioQuestionSpecSchema,
    checklistHeaderSpecSchema,
    userSelectSpecSchema,
    aircraftSelectSpecSchema,
])

export type QuestionType =
    | "text"
    | "multi-select"
    | "radio"
    | "header"
    | "user-select"
    | "aircraft-select"
export type ChecklistQuestionSpec<T extends QuestionType = QuestionType> = Extract<
    z.infer<typeof checklistQuestionSpecSchema>,
    { type: T }
>

export const checklistFormRecordSchema = z.object({
    id: z.uuid(),
    org_id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    deleted_at: z.string().nullish(),
    created_at: z.string(),
    created_by: z.guid(),
    version: z.number(),
    form: z.array(checklistQuestionSpecSchema),
    version_created_at: z.string(),
    version_created_by: z.guid(),
})

export const checklistFormSchema = checklistFormRecordSchema.transform((r) => ({
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    description: r.description,
    deletedAt: r.deleted_at,
    createdAt: r.created_at,
    createdBy: r.created_by,
    version: r.version,
    form: r.form,
    versionCreatedAt: r.version_created_at,
    versionCreatedBy: r.version_created_by,
}))

export const checklistFormSummaryOutputSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    createdAt: z.string(),
    createdBy: z.string(),
    version: z.number(),
})

export const checklistFormSummarySchema = z
    .object({
        id: z.uuid(),
        org_id: z.string(),
        name: z.string(),
        description: z.string().nullish(),
        created_at: z.string(),
        created_by: z.guid(),
        version: z.number(),
    })
    .transform((r): z.infer<typeof checklistFormSummaryOutputSchema> => ({
        id: r.id,
        orgId: r.org_id,
        name: r.name,
        description: r.description,
        createdAt: r.created_at,
        createdBy: r.created_by,
        version: r.version,
    }))

export type ChecklistFormSummary = z.infer<typeof checklistFormSummarySchema>

export const creatableFormRecordSchema = checklistFormRecordSchema.pick({
    name: true,
    description: true,
    form: true,
})

export type ChecklistFormRecord = z.infer<typeof checklistFormRecordSchema>
export type ChecklistForm = z.infer<typeof checklistFormSchema>
export type CreatableFormRecord = z.infer<typeof creatableFormRecordSchema>
